import { useState, useEffect } from 'react';
import { Users, Search, Phone, Mail, MapPin, Plus, Shield } from 'lucide-react';
import { useI18n } from '../i18n';

import { API_BASE } from '../config/api';

interface StaffMember {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    mobilePhone: string | null;
    role: string;
    specialty: string | null;
    licenseNumber: string | null;
    isActive: boolean;
    calendarColor: string;
    defaultLocation: { id: string; name: string } | null;
}

const roleLabels: Record<string, { label: string; badge: string }> = {
    doctor: { label: 'Médico', badge: 'badge-primary' },
    coordinator: { label: 'Coordinador', badge: 'badge-warning' },
    receptionist: { label: 'Recepcionista', badge: 'badge-success' },
    nurse: { label: 'Enfermera', badge: 'badge-info' },
    admin: { label: 'Administrador', badge: 'badge-danger' },
};

export function StaffPage() {
    const { t } = useI18n();
    const [staff, setStaff] = useState<StaffMember[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('');

    useEffect(() => {
        loadStaff();
    }, [roleFilter]);

    async function loadStaff() {
        setLoading(true);
        try {
            const params = roleFilter ? `?role=${roleFilter}` : '';
            const res = await fetch(`${API_BASE}/staff${params}`);
            const json = await res.json();
            setStaff(json.data || []);
        } catch {
            console.warn('Staff API unavailable');
        } finally {
            setLoading(false);
        }
    }

    const filtered = staff.filter(s => {
        if (!search) return true;
        const q = search.toLowerCase();
        return `${s.firstName} ${s.lastName}`.toLowerCase().includes(q) ||
            s.email.toLowerCase().includes(q);
    });

    return (
        <div className="fade-in">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h1 className="page-title"><Users size={22} style={{ display: 'inline', marginRight: 8 }} />{t('nav.staff')}</h1>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
                    <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
                    <input
                        className="form-input"
                        style={{ paddingLeft: 36 }}
                        placeholder="Buscar personal..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
                <select className="form-input" style={{ width: 160 }} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                    <option value="">Todos los roles</option>
                    <option value="doctor">Médico</option>
                    <option value="coordinator">Coordinador</option>
                    <option value="receptionist">Recepcionista</option>
                    <option value="nurse">Enfermera</option>
                    <option value="admin">Administrador</option>
                </select>
            </div>

            {/* Staff Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                {loading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="card" style={{ padding: 24 }}>
                            <div className="skeleton" style={{ height: 80 }} />
                        </div>
                    ))
                ) : filtered.length === 0 ? (
                    <div className="card" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 48, color: 'var(--text-3)' }}>
                        <Users size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                        <p>No se encontró personal</p>
                    </div>
                ) : (
                    filtered.map(member => {
                        const roleInfo = roleLabels[member.role] || { label: member.role, badge: 'badge-neutral' };
                        return (
                            <div key={member.id} className="card" style={{ padding: 20 }}>
                                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                                    <div style={{
                                        width: 48, height: 48, borderRadius: '50%',
                                        background: member.calendarColor || 'var(--accent)',
                                        color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 18, fontWeight: 700, flexShrink: 0,
                                    }}>
                                        {member.firstName[0]}{member.lastName[0]}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                            <h3 style={{ fontWeight: 600, fontSize: 16, margin: 0 }}>
                                                {member.firstName} {member.lastName}
                                            </h3>
                                            <span className={`badge ${roleInfo.badge}`}>{roleInfo.label}</span>
                                        </div>
                                        {member.specialty && (
                                            <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 8 }}>
                                                <Shield size={12} style={{ display: 'inline', marginRight: 4 }} />
                                                {member.specialty}
                                                {member.licenseNumber && ` • Lic. ${member.licenseNumber}`}
                                            </div>
                                        )}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: 'var(--text-3)' }}>
                                            <span><Mail size={12} style={{ display: 'inline', marginRight: 6 }} />{member.email}</span>
                                            {member.mobilePhone && (
                                                <span><Phone size={12} style={{ display: 'inline', marginRight: 6 }} />{member.mobilePhone}</span>
                                            )}
                                            {member.defaultLocation && (
                                                <span><MapPin size={12} style={{ display: 'inline', marginRight: 6 }} />{member.defaultLocation.name}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
